/*
 * What stands where the composer would be when the reader is not in the conversation (UX §8 #8,
 * A-01 §4.2). A channel is readable by everybody in one of its owner groups, so arriving at one by a
 * link or a search hit shows its history — and then a composer that answers "Not sent: Not found."
 * on the first Enter, which was the second half of bug (a).
 *
 * So: no composer, one sentence saying why, and the one thing that changes it. It is the product's one
 * alert shape (VISUAL.md §6.12) — a glyph, a sentence, an action in the slot an alert keeps for one —
 * because a read-only room is a state of the screen and not a notice beside it.
 *
 * Joining is not confirmed and not undone here — a channel is left again from its details view. What
 * replaces the banner is the membership row the join route answers with: the surface above reads that
 * row to decide whether to draw a composer at all, and a reader who has just joined must be able to
 * write without reloading the page.
 */

import type { ConversationId } from "../../../core/store/model.ts"
import { client, useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Alert, Button, FormAlert } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { upsertConversation, upsertSelf } from "../state/reducer.ts"
import type { ConversationView } from "../wire/views.ts"
import { joinConversation } from "./api.ts"
import "./conversation.css"
import { conversationAddress } from "./title.ts"

export function JoinBanner({ conv }: { conv: ConversationId }) {
	const meta = useModel((model) => model.conversations[conv]?.meta) as ConversationView | undefined
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)
	const join = useAsyncAction(async () => {
		const { self } = await joinConversation(conv)
		// Both halves of "the reader is in it now", from the answer rather than from the event that
		// repeats it: the membership row, which decides between this banner and a composer, and the
		// conversation's place in the reader's own list, which is what the sidebar draws a row from.
		client.api.setState((model) => {
			const withSelf = upsertSelf(model, conv, self)
			const view = withSelf.conversations[conv]?.meta as ConversationView | undefined
			return view ? upsertConversation(withSelf, view) : withSelf
		})
	})
	if (!meta) return null

	return (
		<>
			<Alert
				kind="warning"
				className="tb-join"
				action={
					<Button
						variant="primary"
						size="sm"
						label={t("chat.join.action")}
						{...(join.busy ? { busy: true } : {})}
						onClick={() => void join.run()}
					/>
				}
			>
				{t("chat.join.notMember", { conversation: conversationAddress(meta, users, meId) })}{" "}
				{t("chat.join.readOnly")}
			</Alert>
			<FormAlert messageKey={join.error} />
		</>
	)
}
